import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useKeywordSynonyms } from './use-keyword-synonyms';
import { useToast } from '@/components/ui/use-toast';
import { KeywordWithSynonyms } from '@/types/custom';

// TODO: centralize provider
export function useKeywords(deps: any[] = []) {
  const [keywords, setKeywords] = useState<KeywordWithSynonyms[]>([]);
  const [loading, setLoading] = useState(true);
  // keyword synonyms disabled for now:
  // const { synonyms, loading: synonymsLoading } = useKeywordSynonyms();
  const { synonyms, loading: synonymsLoading } = {
    synonyms: [],
    loading: false,
  };

  const { toast } = useToast();

  useEffect(() => {
    fetchKeywords();

    const channel = supabase
      .channel('keywords-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'keywords' },
        fetchKeywords,
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [...deps]);

  // Combine keywords with their synonyms when both are loaded
  useEffect(() => {
    if (!loading && !synonymsLoading && keywords.length > 0) {
      const keywordsWithSynonyms = keywords.map((keyword) => {
        const keywordSynonymsData = synonyms.filter(
          (s) => s.keyword_id === keyword.id,
        );

        const keywordSynonyms = keywordSynonymsData.map((s) => s.synonym);

        return {
          ...keyword,
          synonyms: keywordSynonyms,
          synonymsData: keywordSynonymsData,
        };
      });

      setKeywords(keywordsWithSynonyms);
    }
  }, [loading, synonymsLoading, synonyms]);

  async function fetchKeywords() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('keywords')
        .select('*')
        .order('name');

      if (error) throw error;

      setKeywords(data || []);
    } catch (error) {
      toast({
        title: 'Fout bij het laden van keywords',
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  }

  async function getContentByKeyword(keywordId: string) {
    try {
      // First try to get content from the content_keywords table
      const { data, error } = await supabase
        .from('content_keywords')
        .select('content_id')
        .eq('keyword_id', keywordId);

      if (error) throw error;

      if (data && data.length > 0) {
        const contentIds = data.map((item) => item.content_id);
        const { data: contentData, error: contentError } = await supabase
          .from('content')
          .select('*')
          .in('id', contentIds);

        if (contentError) throw contentError;
        return contentData;
      }

      // If no results from content_keywords, try to find content by keyword name
      const { data: keywordData, error: keywordError } = await supabase
        .from('keywords')
        .select('name')
        .eq('id', keywordId)
        .single();

      if (keywordError) throw keywordError;

      if (keywordData) {
        const keywordName = keywordData.name;

        // Search for content with titles or descriptions containing the keyword
        const { data: contentByName, error: contentByNameError } =
          await supabase
            .from('content')
            .select('*')
            .or(
              `title.ilike.%${keywordName}%,description.ilike.%${keywordName}%`,
            );

        if (contentByNameError) throw contentByNameError;
        return contentByName || [];
      }

      return [];
    } catch (error) {
      console.error('Error fetching content by keyword:', error);
      return [];
    }
  }

  async function searchContentByKeywords(keywordIds: string[]) {
    try {
      const { data, error } = await supabase
        .from('content_keywords')
        .select('content_id')
        .in('keyword_id', keywordIds);

      if (error) throw error;

      if (data && data.length > 0) {
        const contentIds = [...new Set(data.map((item) => item.content_id))];
        const { data: contentData, error: contentError } = await supabase
          .from('content')
          .select('*')
          .in('id', contentIds);

        if (contentError) throw contentError;
        return contentData;
      }
      return [];
    } catch (error) {
      console.error('Error searching content by keywords:', error);
      return [];
    }
  }

  async function addKeywordToContent(contentId: string, keywordId: string) {
    try {
      const { error } = await supabase
        .from('content_keywords')
        .insert([{ content_id: contentId, keyword_id: keywordId }]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error adding keyword to content:', error);
      return false;
    }
  }

  async function removeKeywordFromContent(
    contentId: string,
    keywordId: string,
  ) {
    try {
      const { error } = await supabase
        .from('content_keywords')
        .delete()
        .eq('content_id', contentId)
        .eq('keyword_id', keywordId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing keyword from content:', error);
      return false;
    }
  }

  async function getKeywordsForContent(contentId: string) {
    try {
      const { data, error } = await supabase
        .from('content_keywords')
        .select('keyword_id')
        .eq('content_id', contentId);

      if (error) throw error;

      if (data && data.length > 0) {
        const keywordIds = data.map((item) => item.keyword_id);
        const { data: keywordData, error: keywordError } = await supabase
          .from('keywords')
          .select('*')
          .in('id', keywordIds);

        if (keywordError) throw keywordError;
        return keywordData;
      }
      return [];
    } catch (error) {
      console.error('Error fetching keywords for content:', error);
      return [];
    }
  }

  async function addKeywordToNewsItem(newsItemId: string, keywordId: string) {
    try {
      const { error } = await supabase
        .from('news_item_keywords')
        .insert([{ news_item_id: newsItemId, keyword_id: keywordId }]);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error adding keyword to news item:', error);
      return false;
    }
  }

  async function removeKeywordFromNewsItem(newsItemId: string, keywordId: string) {
    try {
      const { error } = await supabase
        .from('news_item_keywords')
        .delete()
        .eq('news_item_id', newsItemId)
        .eq('keyword_id', keywordId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing keyword from news item:', error);
      return false;
    }
  }

  async function getKeywordsForNewsItem(newsItemId: string) {
    try {
      const { data, error } = await supabase
        .from('news_item_keywords')
        .select('keyword_id')
        .eq('news_item_id', newsItemId);
      if (error) throw error;
      if (data && data.length > 0) {
        const keywordIds = data.map((item) => item.keyword_id);
        const { data: keywordData, error: keywordError } = await supabase
          .from('keywords')
          .select('*')
          .in('id', keywordIds);
        if (keywordError) throw keywordError;
        return keywordData;
      }
      return [];
    } catch (error) {
      console.error('Error fetching keywords for news item:', error);
      return [];
    }
  }

  return {
    keywords,
    loading,
    getContentByKeyword,
    searchContentByKeywords,
    addKeywordToContent,
    removeKeywordFromContent,
    getKeywordsForContent,
    addKeywordToNewsItem,
    removeKeywordFromNewsItem,
    getKeywordsForNewsItem,
  };
}
